import { useEffect, useRef, useState } from 'react'
import { api } from '@/api'
import { Icon } from '@/components/icons'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel } from '@/components/studio'
import { Button, Chips } from '@/components/ui'
import {
  CLONE_SCENES,
  DEFAULT_PREVIEW_TEXT,
  PREVIEW_MAX_CHARS,
  REF_MAX_SECONDS,
  REF_MIN_SECONDS,
  pickScriptLine,
} from '@/config/voiceClone'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { useJobCenter, type Job } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { useToast } from '@/context/ToastContext'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { usePricePreview } from '@/hooks/usePricePreview'
import { formatClock, useRecorder } from '@/hooks/useRecorder'
import { invalidateVoices } from '@/hooks/useVoices'
import { refillFromInputs } from '@/lib/refill'

/**
 * 声音克隆工作台（`/tts/clone`）—— 念一段话，建一个能长期用的音色。
 *
 * ## 为什么它从「音色库抽屉里的小表单」搬成了一个独立页面
 *
 * 2026-10-09 用户给了参考站 `minimax.cn/audio/voices-cloning` 的截图，
 * 原话「学一下，是不是新页面更方便」。三条判据都指向独立页：
 *
 *  ① **参考站自己就是独立页** —— 它的左侧栏里「音色库」与「声音克隆」是
 *     **并列的两项**。上一版把克隆塞进音色库抽屉，头注里还写着「参考站也是
 *     放在音色库里的」—— 那句是错的，这一版一并纠正。
 *  ② **它是一段流程，不是一个表单** —— 「选场景 → 照着念 / 录 → 定试听文本 →
 *     确认授权 → 生成」。抽屉里只放得下最简形态（上传 + 命名 + 勾选），
 *     流程表达不出来。
 *  ③ **两个动作的心智不同** —— 音色库是「**挑**音色」，克隆是「**造**音色」。
 *     混在一个抽屉里，用户得同时理解两种模式。
 *
 * ⇒ 音色库「我的音色」页签退化成**清单 + 空态跳转**（见 `VoiceLibrary`），
 *   克隆的入口只剩这一页。
 *
 * ## 🔴 它是全站**第 2 个**「一屏到底」的生成页（2026-10-09 用户拍板）
 *
 * 用户当天第二次给参考站截图，原话「**这个还是按截图的打，留个本页历史就可以**」。
 * 截图是一屏：场景 + 朗读 + 试听文本 + 授权 + 底部「生成」全在一屏，没有分步。
 * 所以本页**去掉 `WorkflowTabs`**，与 `/tts` 同档 —— 这是**有记录的架构例外**：
 *
 *  · `scripts/smoke-nav.ts` 里那份 `FLAT_ALLOWED` 从「只对 `/tts` 开口子」
 *    扩成本页一份（那条通用禁令仍留着，**新增**的扁平页照样当场炸）；
 *  · `step_voice_clone` 这个 localStorage 键随之作废 —— 页签没了就没有「第几步」。
 *    刻意**不写迁移**：它只是个整数下标，留着不读不会被任何人看见。
 *  · 提交按钮与报价从右栏顶部**搬到表单底部条**（`.vclone-bar`）。这一条与
 *    「报价常驻右栏」不冲突：那条铁律守的是**多页签**页面「任何一页都看得见价格」，
 *    而一屏式没有「别的页签」这回事，整张表单同屏可见（`/tts` 同理）。
 *
 * 「**留个本页历史就可以**」落成右栏**只有 `StudioPanel`**：去掉上一版挂的
 * 提交坞（`{submitDock}`）与「我克隆的音色」卡（`head`）——
 * 前者搬进了底部条，后者与「语音合成 → 音色库 → 我的音色」是同一份清单，
 * 留两处迟早不同步。右栏只剩「本轮结果 + 本页历史」，正好。
 *
 * ## 三件必须守住的事
 *
 * ① **两条路的音频形态不一样，但都必须「本地先备料」**。
 *    录音出来的是 webm（Chrome），上传的是用户自己的文件 —— 上游只收
 *    mp3/m4a/wav。**转码放在服务端**（`backend/app/audio.py`），这里只把
 *    真实 MIME 后缀带上，别在文件名上撒谎（见 `useRecorder` 的注释）。
 *
 * ② **「10 秒 ~ 5 分钟」是上游的硬约束，不是装饰**。录音这一路前端**当场**卡住
 *    （时长就在计时器里，免费且准确）；上传那一路前端读不出时长，
 *    交给后端 `audio.prepare_reference` 判 —— 那里的报错文案是写给人看的。
 *    刻意**不**在浏览器里解音频时长：`MediaRecorder` 产出的 webm 头部没有
 *    duration，`<audio>` 读出来是 `Infinity`，靠它判卡会变成「明明够长却不让提交」。
 *
 * ③ **授权勾选是硬门槛**，与数字人页的「形象克隆」同一类红线。前端拦住只是第一道，
 *    后端 `_run_clone_phase` 里还有第二道（`inputs.consent` 必须为 true）。
 *    ⚠️ 参考站那句「开始录音即表示您已取得声音授权」**没有照搬** ——
 *    它把「按下录音键」当成了授权的意思表示，而本项目里授权是一次**显式的勾选**。
 *    两种口径同时出现在一屏，用户会以为随便按哪个都算数。
 *
 * ## 音色名：**自动生成，不占界面**
 *
 * 截图里没有命名框，用户也选了「自动命名，不占界面」（2026-10-09）。
 * 名字只是本地记账表（`voice_clones.voice_name`）里的一个标签 ——
 * 上游只认 `voice_id`，中文名根本不发过去（见 `jobrunner._run_clone_phase`）。
 * 所以提交那一刻按时间拼一个 `我的音色 · MM-DD HH:mm`，用户要认哪条看时间就行。
 *
 * ## 产出与「我的音色」的关系
 *
 * 克隆是**后台任务**：提交那一刻音色还不存在。所以这里挂了一个**自动失效**：
 * 本页任务从「进行中」翻成「成功」时替用户失效一次音色缓存 ——
 * 判据是 `succeeded` 计数变大，不是「提交过就算」。之后在「语音合成」页打开
 * 音色库就能看到新音色（那一页的「刷新」也在，这里省的是这一次手点）。
 */

/** 页面键。**与 `sourcePage` / 草稿键是同一个字符串**，三处一起改。 */
const PAGE_KEY = 'voice-clone'

/**
 * 任务档位键（`app_slug`）。与页面键**刻意不同**：
 * 页面可以改名 / 合并，而库里已经落下的 `app_slug` 改不了 ——
 * 后端 `registry.APP_KIND` 里注册的是 `tts-clone`，这里必须逐字对上。
 */
const APP_SLUG = 'tts-clone'

type FormState = {
  /** 参考音频来源。录音是主路（参考站的主按钮就是麦克风），上传是兜底。 */
  source: 'record' | 'upload'
  /** 当前场景；`''` = 随机（跨场景抽） */
  scene: string
  /** 照着念的那段。**纯界面提词器，不进后端** —— 见 `config/voiceClone.ts` 头注 */
  script: string
  /** 试听文本。**会**进 `inputs.previewText`，决定预览音频念什么 */
  previewText: string
}

const DEFAULTS: FormState = {
  source: 'record',
  scene: '',
  script: pickScriptLine('').text,
  previewText: DEFAULT_PREVIEW_TEXT,
}

/** 「随机」不是场景，是「跨场景抽一条」这个动作 —— 所以它的 value 是空串。 */
const SCENE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '随机' },
  ...CLONE_SCENES.map((s) => ({ value: s.id, label: s.label })),
]

/**
 * 自动音色名 —— 提交那一刻按本地时间拼一个，只进本地记账表。
 *
 * 为什么带时间：它会出现在「语音合成 → 音色库 → 我的音色」那张清单里，
 * 一列 `我的音色 · 10-09 17:20` 至少能让用户认出「哪个是刚才建的」。
 * 名字只是标签，**重名不会冲突**（上游按 ID 区分）。
 */
function autoVoiceName(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `我的音色 · ${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function VoiceCloneStudio() {
  const { value: form, patch, reset } = useDraft(PAGE_KEY, DEFAULTS)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  /**
   * ⚠️ 克隆的**参考音频与自动音色名**不在表单里（一个是录/传的文件，一个是提交时按时间拼的），
   * 所以「生成成功后自动清空」清的只是表单字段；那两样本来就不落草稿、不受影响。
   * 指纹比对见 `useGenerateFlow` 看门狗 —— 跑这一分钟里用户又改了试听文本就不清。
   */
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  const toast = useToast()
  const rec = useRecorder()

  const [file, setFile] = useState<File | null>(null)
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const scriptRef = useRef<HTMLTextAreaElement | null>(null)

  /** 只有一档音频模型，但仍然走真源 —— 清单里没有它时会回落到 `DEFAULT_FAMILY_ID`。 */
  const { normalize: normalizeModel } = useModels()
  const modelId = normalizeModel(DEFAULT_FAMILY_ID.audio, 'audio')

  const jobs = jobsFor(PAGE_KEY)
  const outputs = outputsFor(PAGE_KEY)
  const price = usePricePreview({ appSlug: APP_SLUG, model: modelId, outputs: 1 })

  /**
   * 本页有任务**刚刚成功**时，替用户失效一次音色缓存。
   *
   * 判据是「成功数变多了」，不是「提交过就失效」—— 后者在提交那一刻就跑了，
   * 而那时音色还没建出来（克隆要一分钟上下），失效等于白跑一趟。
   * `useRef` 初值取当前值：历史记录里那些老任务不该在进页面时触发一次无效失效。
   */
  const succeeded = jobs.filter((j) => j.state === 'succeeded').length
  const prevSucceeded = useRef(succeeded)
  useEffect(() => {
    if (succeeded > prevSucceeded.current) {
      invalidateVoices()
      toast.success('克隆完成 —— 到「语音合成 → 音色库 → 我的音色」里就能选到它了')
    }
    prevSucceeded.current = succeeded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [succeeded])

  /**
   * 朗读卡里的提词框**跟着内容长**（自动高度）。
   *
   * 为什么值得一段 `useEffect`：提词文案有长有短（12 条里短的 30 字、长的 60 字），
   * 而 `textarea` 不会自己长高 —— 固定 `min-height` 只有两种结局：
   * 短文案底下空一大块，或者长文案出现滚动条。参考站那张卡是**贴着文字**的
   * （卡片下沿到麦克风药丸只有约 24px），固定高度做不出那个观感。
   *
   * ⚠️ 先 `height: auto` 再取 `scrollHeight`：不先归零的话，内容变短时
   * `scrollHeight` 会停在「上一次的更大值」上，框只会越长越高。
   * 依赖是 `form.script` 而不是 `rec` —— 点场景 chip 换词走的是 `patch`，
   * 一样会触发。
   */
  useEffect(() => {
    const el = scriptRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [form.script])

  const preview = form.previewText.trim() || DEFAULT_PREVIEW_TEXT

  /**
   * 真正的输入模式。**浏览器不给用麦克风时一律走上传** —— 摆一颗点了必然报错的
   * 录音键，比不摆更让人困惑（`useRecorder` 已经把「不支持」探好了）。
   */
  const recMode = form.source === 'record' && rec.supported

  /** 录音路的前端时长闸门 —— 时长就在计时器里，白拿的准确性。 */
  const recSecs = rec.seconds
  const durIssue =
    recMode && rec.phase === 'ready'
      ? recSecs < REF_MIN_SECONDS
        ? `录到的只有 ${recSecs} 秒 —— 上游要求至少 ${REF_MIN_SECONDS} 秒，太短提取不出音色`
        : recSecs > REF_MAX_SECONDS
          ? `录到了 ${(recSecs / 60).toFixed(1)} 分钟 —— 上游要求不超过 ${REF_MAX_SECONDS / 60} 分钟`
          : null
      : null

  const hasAudio = recMode ? !!rec.blob : !!file
  const blocking =
    rec.phase === 'recording'
      ? '正在录音 —— 先点「停止」'
      : (durIssue ??
        (!hasAudio
          ? recMode
            ? `请先录一段声音（至少 ${REF_MIN_SECONDS} 秒）`
            : '请先选一个音频文件'
          : !consent
            ? '请先勾选下面的授权确认'
            : null))
  const canSubmit = !blocking && !busy

  /**
   * 点场景即抽一条新朗读文本。
   *
   * 为什么点**已选中**的那个场景也要重抽：这样「换一条」就不用再占一颗按钮 ——
   * 用户想换词时的动作本来就是「再看一眼这个场景还有什么」。
   * `avoid` 传当前这条，避免连点两下原地不动。
   */
  const pickScene = (sceneId: string) =>
    patch({ scene: sceneId, script: pickScriptLine(sceneId, form.script).text })

  /** 真正要上传的那个文件（录音 或 选中的文件）。 */
  const resolveAudio = (): File | null => {
    if (recMode) {
      if (!rec.blob) return null
      // 后缀按**实际 MIME** 推（`useRecorder` 给的就是它）—— 见页头注 ①
      return new File([rec.blob], `clone-ref-${Date.now()}.${rec.ext}`, {
        type: rec.blob.type || 'audio/webm',
      })
    }
    return file
  }

  /**
   * 点「生成音色」：**先上传，再弹报价确认**。
   *
   * 为什么上传放在确认之前：确认弹窗里要报的是「这一单」的价格与将发送的试听文本，
   * 而载荷里必须带着参考音频的地址。上传本身不花钱（本地接口），
   * 用户取消确认只是白传一次文件 —— 反过来（先确认后上传）则要在确认之后
   * 再做一次可能失败的网络动作，那时用户已经被告知「要花钱了」。
   */
  const doClone = async () => {
    const audio = resolveAudio()
    if (!audio) return
    // 名字在**提交这一刻**才定：时间戳才有意义（页头注「音色名」）
    const voiceName = autoVoiceName()
    setBusy(true)
    try {
      const up = await api.upload(audio)
      const refAudio = up.localUrl ?? up.downloadUrl
      flow.request({
        info: {
          title: '声音克隆',
          appSlug: APP_SLUG,
          model: modelId,
          outputs: 1,
          refImages: 0,
          prompt: preview,
          promptLabel: '将用于试听的文本',
          price: price?.estimatedPrice ?? 0,
          basis: price?.basis,
        },
        args: {
          appSlug: APP_SLUG,
          title: `声音克隆 · ${voiceName}`,
          sourcePage: PAGE_KEY,
          model: modelId,
          // 克隆没有「要被念出来的正文」—— 参考音频才是输入，所以 prompt 留空
          prompt: '',
          inputs: {
            // 🔴 后端 `_run_audio_phase` 靠它分流到 `_run_clone_phase`，改名要两边一起改
            op: 'clone',
            voiceName,
            refAudio,
            consent: true,
            previewText: preview,
          },
          outputs: 1,
          // 上传 + 建音色 + 试听合成三步，给一个宽松的估算值
          estimatedMs: 90_000,
        },
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '参考音频上传失败')
    } finally {
      setBusy(false)
    }
  }

  /* ------------------------------ 录音区（麦克风药丸） ------------------------------ */

  /**
   * 三种录音态共用**同一个药丸、同一个位置**（待录 / 录音中 / 录完重录），
   * 只换图标、配色与那一行字 —— 键位置不动，用户不会因为「键跑哪儿去了」而误点。
   *
   * 🔴 为什么是**宽药丸**而不是圆键（2026-10-09 第二次对齐参考站）：
   * 第一次量参考站时把「麦克风图标」当成了按钮本体，得出「那颗约 46px」，
   * 于是做了一颗 56px 的圆键。按真机截图重测（原图 1255px 宽，Windows 125%
   * 缩放下 DPR≈1.25）后，那其实是一枚 **274×76 的白底药丸**：
   * 里面竖排「麦克风图标 + 一行小字」，圆角是 pill。它在卡片里是**唯一的主目标**，
   * 76px 高不是为了好看 —— 圆键 56px 在居中的位置像装饰，
   * 而这个尺寸才像「按一下就开始录」。
   *
   * ⚠️ 药丸里那行字**不是**参考站那句「开始录音即表示您已取得声音授权」——
   * 理由见页头注 ③（本项目授权是一次显式勾选，两种口径同屏会互相抵消）。
   * 这里放的是**操作提示**（还差几秒 / 点这里重录），授权仍然只由下面那个勾选框表达。
   */
  const micArea =
    rec.phase === 'recording' ? (
      <button
        type="button"
        className="vclone__mic"
        data-state="recording"
        onClick={rec.stop}
        title="停止录音"
        data-clone-stop="1"
      >
        <Icon name="stop" size={26} className="vclone__micIcon" />
        <span className="vclone__micText">
          <span className="vclone__clock" data-clone-clock="1">
            {formatClock(rec.seconds)}
          </span>
          {rec.seconds < REF_MIN_SECONDS
            ? ` · 还差 ${REF_MIN_SECONDS - rec.seconds} 秒`
            : ' · 够长了，随时可以停'}
        </span>
      </button>
    ) : (
      <button
        type="button"
        className="vclone__mic"
        data-state={rec.phase === 'ready' ? 'ready' : 'idle'}
        onClick={() => (rec.phase === 'ready' ? rec.reset() : void rec.start())}
        title={rec.phase === 'ready' ? '重录' : '开始录音'}
        data-clone-record="1"
      >
        <Icon
          name={rec.phase === 'ready' ? 'refresh' : 'mic'}
          size={26}
          className="vclone__micIcon"
        />
        <span className="vclone__micText">
          {rec.phase === 'ready'
            ? `已录 ${recSecs} 秒 · 点这里重录`
            : `点一下开始录音，至少念 ${REF_MIN_SECONDS} 秒`}
        </span>
      </button>
    )

  /* ------------------------------ 左列 · 编辑区 ------------------------------ */

  const composer = (
    <>
      <header className="vclone-head">
        <h1 className="vclone-head__title">声音克隆</h1>
        <p className="vclone-head__sub">朗读一段文字，即可克隆你的专属声音</p>
      </header>

      {/* ① 选择场景并朗读 */}
      <section className="vclone-sec">
        <div className="vclone-sec__label">选择场景并朗读</div>
        {/*
          ⚠️ 用全站唯一的 `Chips`，不自己拼 `<button className="chip">`（铁律 3）。
          所以这一排**没有参考站那些小图标** —— 那是它自己的实现，本项目不为此开第二份。
        */}
        <Chips
          value={form.scene}
          onChange={pickScene}
          options={SCENE_OPTIONS}
          titles={{ '': '从全部场景里随机抽一条' }}
        />

        <div className="vclone-read">
          <textarea
            ref={scriptRef}
            className="vclone-read__script"
            value={form.script}
            onChange={(e) => patch({ script: e.target.value })}
            aria-label="朗读引导文本"
            data-clone-script="1"
          />

          {/* 麦克风主路；浏览器不给用麦克风时这一块换成上传（见 `recMode`） */}
          {recMode ? (
            <div className="vclone__rec">{micArea}</div>
          ) : (
            <label className="vclone__drop" data-clone-drop="1">
              <Icon name="upload" size={18} />
              <span className="vclone__dropMain">
                {file ? file.name : '点击选择一个音频文件'}
              </span>
              <span className="text-sm muted">
                {file
                  ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                  : `mp3 / m4a / wav，${REF_MIN_SECONDS} 秒 ~ ${REF_MAX_SECONDS / 60} 分钟`}
              </span>
              <input
                ref={fileRef}
                type="file"
                accept="audio/*,.mp3,.m4a,.wav"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                data-clone-file="1"
              />
            </label>
          )}

          {/* 录完先就地听一遍 —— 这是用户唯一能自检「录清楚了吗」的机会 */}
          {recMode && rec.blob ? (
            <audio className="vclone__playback" src={URL.createObjectURL(rec.blob)} controls />
          ) : null}

          {rec.error ? (
            <div className="vclone__err">
              <Icon name="alert" size={13} />
              <span>{rec.error}</span>
            </div>
          ) : null}

          {/*
            录音 / 上传的**切换入口**。刻意不做成参考站那种「两个模式」的显式控件：
            截图里只有麦克风，上传是兜底 —— 一条文字链最不喧宾夺主，
            但**必须在**（内网 http、老浏览器、没有麦的机器都只能走它）。
          */}
          <div className="vclone-read__foot">
            {rec.supported ? (
              recMode ? (
                <button type="button" className="linkbtn" onClick={() => patch({ source: 'upload' })}>
                  没有麦克风？改用上传音频
                </button>
              ) : (
                <button type="button" className="linkbtn" onClick={() => patch({ source: 'record' })}>
                  改回现场录音
                </button>
              )
            ) : (
              <span className="text-sm muted">
                这个浏览器不能录音（需要 https 或 localhost）—— 请上传一段音频
              </span>
            )}
            <span className="text-sm muted">
              录音出来是浏览器格式，提交后由服务端转成上游认的 mp3，你什么都不用做。
            </span>
          </div>
        </div>
      </section>

      {/* ② 试听文本 */}
      <section className="vclone-sec">
        <div className="vclone-sec__label">试听文本</div>
        <div className="vclone-preview">
          <textarea
            className="vclone-preview__input"
            value={form.previewText}
            maxLength={PREVIEW_MAX_CHARS}
            placeholder={DEFAULT_PREVIEW_TEXT}
            onChange={(e) => patch({ previewText: e.target.value })}
            aria-label="试听文本"
            data-clone-preview="1"
          />
          <div className="vclone-preview__meta">
            <span className="text-sm muted">音色建好后，用这段合成一段给你听</span>
            <span className="grow" />
            {/*
              参考站这一行右侧有一颗「重新生成试听文本」的骰子。我们**没有**
              第二条可换的试听文本（不像朗读引导有 12 条），所以那颗骰子在这里
              无事可做 —— 换成一颗真的有用的「恢复默认」：用户改乱了能一键回来，
              没改过时**不出现**（一个空操作摆在收费按钮旁边只会招人怀疑）。
            */}
            {form.previewText !== DEFAULT_PREVIEW_TEXT && form.previewText.length > 0 ? (
              <button
                type="button"
                className="linkbtn"
                title="把试听文本换回默认那句"
                onClick={() => patch({ previewText: DEFAULT_PREVIEW_TEXT })}
              >
                恢复默认
              </button>
            ) : null}
            <span className="vclone-preview__count">
              {form.previewText.length} / {PREVIEW_MAX_CHARS} 字符
            </span>
          </div>
        </div>
      </section>

      {/*
        合规红线 —— 与数字人页「形象克隆」同一块的存在理由：克隆他人声音属于
        声音权使用，而授权勾选是**提交的硬前提**（后端 `_run_clone_phase` 有第二道）。
        它必须和提交按钮同屏能看见：一屏式版面里，它就落在底部条正上方。
      */}
      <div className="vclone__legal">
        <label className="vclone-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-clone-consent="1"
          />
          <span>
            我已阅读并同意《声音克隆功能使用规则》；我确认已取得该声音本人的授权并已归档凭证，
            同意仅将其用于本店商品内容，并承诺不把本功能用于违法违规目的。
          </span>
        </label>
        <div className="text-sm muted" style={{ marginTop: 10, lineHeight: 1.75 }}>
          未获授权的声音一律不得克隆 —— 这是法律问题，不是偏好问题。发布时请按平台要求标注 AI 生成内容。
        </div>
      </div>

      {/* ③ 底部工具条：阻断原因（或自检提示）+ 报价 + 生成 */}
      <div className="vclone-bar">
        <span className="vclone-bar__hint text-sm muted">
          {/* 有东西拦着就说清楚为什么；没有就说下一步会发生什么 */}
          {blocking ?? '克隆是后台任务：建成后右侧结果里会出现一段试听，通常一分钟内完成'}
        </span>
        <span className="grow" />
        <span className="tts-cost">
          {price?.basis ?? '预估消耗'}
          <b>¥{(price?.estimatedPrice ?? 0).toFixed(2)}</b>
        </span>
        <Button
          variant="primary"
          size="lg"
          icon="sparkles"
          loading={busy}
          disabled={!canSubmit}
          onClick={() => void doClone()}
          title={blocking ?? undefined}
          data-clone-generate="1"
        >
          生成音色
        </Button>
      </div>
    </>
  )

  return (
    <div className="page page--wide">
      {/*
        右栏**只有 `StudioPanel`**（用户口径「留个本页历史就可以」）：
        提交坞搬进了底部条（`.vclone-bar`），「我克隆的音色」卡去掉 ——
        那份清单在「语音合成 → 音色库 → 我的音色」里已经有了，两处迟早不同步。
      */}
      <GeneratorFrame
        className="vclone-grid"
        form={composer}
        panel={
          <StudioPanel
            jobs={jobs}
            outputs={outputs}
            onCancel={cancel}
            onClear={clearResults}
            onRedo={(job: Job) => {
              patch(refillFromInputs(form, job.inputs))
              toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
            }}
            kind="audio"
            activeHint="克隆要上传参考音频、建音色、再合成一段试听 —— 通常一分钟内完成"
            resultTitle="本轮结果"
            resultEmpty="左侧录好或选好参考音频、勾选授权后点「生成音色」，试听音频会出现在这里。"
          />
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
